import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  Image,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import api from '../../services/api';

interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in MB
  uploadType?: 'image' | 'document' | 'profile';
  onUpload?: (files: UploadedFile[]) => void;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
  showPreview?: boolean;
}

interface UploadedFile {
  url: string;
  filename: string;
  size: number;
  mimetype: string;
  type?: string;
}

interface FileWithPreview extends File {
  preview?: string;
  id: string;
  uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  uploadedFile?: UploadedFile;
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept = 'image/*',
  multiple = false,
  maxFiles = 5,
  maxSize = 5, // 5MB default
  uploadType = 'image',
  onUpload,
  onError,
  className = '',
  disabled = false,
  showPreview = true
}) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }

    // Check file type based on upload type
    const validTypes = {
      image: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      document: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ],
      profile: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    };

    if (!validTypes[uploadType].includes(file.type)) {
      return `Invalid file type. Allowed types: ${validTypes[uploadType].join(', ')}`;
    }

    return null;
  };

  const createFilePreview = (file: File): FileWithPreview => {
    const fileWithPreview = file as FileWithPreview;
    fileWithPreview.id = Math.random().toString(36).substr(2, 9);
    fileWithPreview.uploadStatus = 'pending';

    if (file.type.startsWith('image/')) {
      fileWithPreview.preview = URL.createObjectURL(file);
    }

    return fileWithPreview;
  };

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    
    // Check total file count
    if (!multiple && fileArray.length > 1) {
      onError?.('Only one file is allowed');
      return;
    }

    if (files.length + fileArray.length > maxFiles) {
      onError?.(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const validFiles: FileWithPreview[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(createFilePreview(file));
      }
    });

    if (errors.length > 0) {
      onError?.(errors.join(', '));
    }

    if (validFiles.length > 0) {
      setFiles(prev => multiple ? [...prev, ...validFiles] : validFiles);
    }
  }, [files.length, maxFiles, maxSize, multiple, onError, uploadType]);

  const uploadFile = async (file: FileWithPreview): Promise<UploadedFile> => {
    const formData = new FormData();
    // Backend /upload/single expects field 'file' for all upload types
    formData.append('file', file);
    formData.append('folder', uploadType === 'profile' ? 'avatars' : uploadType === 'document' ? 'documents' : 'uploads');

    const res = await api.post('/upload/single', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (!res.data.success) {
      throw new Error(res.data.error?.message || 'Upload failed');
    }

    const { url, publicId, resourceType } = res.data.data;
    return { url, filename: publicId || url, size: file.size, mimetype: file.type, type: resourceType };
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    const uploadedFiles: UploadedFile[] = [];

    try {
      // Update all files to uploading status
      setFiles(prev => prev.map(f => ({ ...f, uploadStatus: 'uploading' as const })));

      // Upload files sequentially to avoid overwhelming the server
      for (const file of files) {
        try {
          const uploadedFile = await uploadFile(file);
          uploadedFiles.push(uploadedFile);
          
          // Update file status to success
          setFiles(prev => prev.map(f => 
            f.id === file.id 
              ? { ...f, uploadStatus: 'success' as const, uploadedFile }
              : f
          ));
        } catch (error) {
          // Update file status to error
          setFiles(prev => prev.map(f => 
            f.id === file.id 
              ? { ...f, uploadStatus: 'error' as const, error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          ));
        }
      }

      if (uploadedFiles.length > 0) {
        onUpload?.(uploadedFiles);
      }
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const clearFiles = () => {
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setFiles([]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const getFileIcon = (file: FileWithPreview) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-6 h-6" />;
    }
    return <FileText className="w-6 h-6" />;
  };

  const getStatusIcon = (status: FileWithPreview['uploadStatus']) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : disabled
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled}
          className="hidden"
        />

        <div className="flex flex-col items-center space-y-2">
          <Upload className={`w-12 h-12 ${disabled ? 'text-gray-400' : 'text-gray-500'}`} />
          <div>
            <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
              {isDragOver ? 'Drop files here' : 'Drag and drop files here, or'}
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className={`text-sm font-medium ${
                disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:text-blue-500'
              }`}
            >
              browse files
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Max {maxFiles} files, {maxSize}MB each
          </p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && showPreview && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              Selected Files ({files.length})
            </h4>
            <button
              type="button"
              onClick={clearFiles}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                {/* File Preview/Icon */}
                <div className="flex-shrink-0">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      {getFileIcon(file)}
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                  {file.uploadStatus === 'error' && file.error && (
                    <p className="text-xs text-red-500 mt-1">{file.error}</p>
                  )}
                </div>

                {/* Status */}
                <div className="flex items-center space-x-2">
                  {getStatusIcon(file.uploadStatus)}
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    disabled={file.uploadStatus === 'uploading'}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleUpload}
              disabled={isUploading || files.length === 0 || files.every(f => f.uploadStatus === 'success')}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;